import { useEffect, useState } from 'react';
import { AlertTriangle, FileWarning, X } from 'lucide-react';
import {
  type FailedSyncEvent,
  localStore,
  subscribeFailedEvents,
} from '../db';

/**
 * "Recover failed edits" — sticky review surface for permanently-dropped
 * sync events. The status light goes red while this list is non-empty;
 * dismissing the modal without clicking "Acknowledge all" leaves the red
 * state in place. See plan F4.
 */
export function FailedEditsModal({ onClose }: { onClose: () => void }) {
  const [events, setEvents] = useState<FailedSyncEvent[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function refresh() {
      try {
        const next = await localStore.listFailedEvents();
        if (!cancelled) setEvents(next);
      } catch {
        /* ignore */
      }
    }
    void refresh();
    const off = subscribeFailedEvents(() => void refresh());
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      cancelled = true;
      off();
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  async function acknowledge() {
    setBusy(true);
    try {
      await localStore.clearFailedEvents();
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
      <div className="card w-full max-w-lg p-5 max-h-[80vh] flex flex-col">
        <div className="flex items-center gap-2 mb-3">
          <FileWarning className="text-red-400" size={18} />
          <h2 className="font-semibold">Recover failed edits</h2>
          <button onClick={onClose} className="btn-ghost ml-auto !p-1" aria-label="Close">
            <X size={14} />
          </button>
        </div>

        {events.length === 0 ? (
          <p className="text-sm text-ink-300">
            Nothing to recover. The status light will return to green.
          </p>
        ) : (
          <>
            <div className="flex items-start gap-2 rounded border border-red-900 bg-red-950/40 p-3 mb-3 text-sm text-red-200">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
              <div>
                <strong>
                  {events.length === 1
                    ? '1 edit could not be saved'
                    : `${events.length} edits could not be saved`}
                </strong>
                . The server permanently rejected these. Where possible,
                re-create the affected pages or content from your own
                records, then click <em>Acknowledge all</em>.
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1 space-y-2">
              {events.map((event) => (
                <div
                  key={event.eventId}
                  className="rounded border border-ink-700 bg-ink-800/60 p-3 text-sm"
                >
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="font-mono text-xs text-red-300">
                      HTTP {event.status || '—'}
                    </span>
                    <span className="text-ink-400 text-xs">
                      {new Date(event.at).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-ink-200">
                    {describeEvent(event)}
                  </div>
                  {event.message && (
                    <div className="text-xs text-ink-400 mt-1 break-all">
                      {event.message}
                    </div>
                  )}
                  {(event.documentId || event.customerId) && (
                    <div className="text-xs text-ink-500 mt-1 font-mono break-all">
                      {event.documentId && <>doc {event.documentId}</>}
                      {event.documentId && event.customerId && <> · </>}
                      {event.customerId && <>customer {event.customerId}</>}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <button className="btn-ghost flex-1" onClick={onClose}>
                Close
              </button>
              <button
                className="btn-primary flex-1"
                onClick={acknowledge}
                disabled={busy}
              >
                {busy ? 'Clearing…' : 'Acknowledge all'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function describeEvent(event: FailedSyncEvent): string {
  if (event.source === 'snapshot') {
    return 'Page body edit could not be saved.';
  }
  switch (event.opType) {
    case 'upsert_customer':
      return event.isNew ? 'Customer create failed.' : 'Customer update failed.';
    case 'delete_customer':
      return 'Customer delete failed.';
    case 'upsert_document':
      return event.isNew ? 'Page create failed.' : 'Page update failed.';
    case 'delete_document':
      return 'Page delete failed.';
    default:
      return 'Sync operation failed.';
  }
}
